import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Trash2, 
  Edit, 
  Save, 
  X, 
  Check,
  BookOpen,
  Calculator,
  Brain,
  Target,
  AlertCircle
} from 'lucide-react';

const DynamicSectionConfig = ({ 
  sections = [], 
  totalQuestions = 0, 
  onSectionsChange,
  onValidationChange 
}) => {
  const [localSections, setLocalSections] = useState(sections);
  const [editingIndex, setEditingIndex] = useState(-1);
  const [errors, setErrors] = useState({});

  // Default section types with icons
  const sectionTypes = [
    { id: 'english', name: 'English Language', icon: BookOpen, color: 'text-blue-600' },
    { id: 'numerical', name: 'Numerical Ability', icon: Calculator, color: 'text-green-600' },
    { id: 'reasoning', name: 'Reasoning Ability', icon: Brain, color: 'text-purple-600' },
    { id: 'general', name: 'General Knowledge', icon: Target, color: 'text-orange-600' },
    { id: 'custom', name: 'Custom Section', icon: BookOpen, color: 'text-gray-600' }
  ];

  useEffect(() => {
    setLocalSections(sections);
  }, [sections]);

  useEffect(() => {
    validateSections();
  }, [localSections, totalQuestions]);

  const validateSections = () => {
    const newErrors = {};
    let totalCovered = 0;
    const usedRanges = new Set();

    localSections.forEach((section, index) => {
      // Check for empty fields
      if (!section.name.trim()) {
        newErrors[`name_${index}`] = 'Section name is required';
      }
      if (!section.startQuestion || !section.endQuestion) {
        newErrors[`range_${index}`] = 'Question range is required';
      }

      // Check for valid range
      if (section.startQuestion && section.endQuestion) {
        const start = parseInt(section.startQuestion);
        const end = parseInt(section.endQuestion);
        
        if (start > end) {
          newErrors[`range_${index}`] = 'Start question must be less than end question';
        }
        if (start < 1 || end > totalQuestions) {
          newErrors[`range_${index}`] = `Range must be between 1 and ${totalQuestions}`;
        }

        // Check for overlapping ranges
        const rangeKey = `${start}-${end}`;
        if (usedRanges.has(rangeKey)) {
          newErrors[`range_${index}`] = 'Question ranges cannot overlap';
        }
        usedRanges.add(rangeKey);

        totalCovered += (end - start + 1);
      }
    });

    // Check if all questions are covered
    if (totalCovered !== totalQuestions) {
      newErrors.general = `Sections must cover exactly ${totalQuestions} questions. Currently covering ${totalCovered} questions.`;
    }

    setErrors(newErrors);
    onValidationChange(Object.keys(newErrors).length === 0);
  };

  const addSection = () => {
    const newSection = {
      id: `section_${Date.now()}`,
      name: '',
      type: 'custom',
      startQuestion: '',
      endQuestion: '',
      totalMarks: 0,
      totalQuestions: 0,
      timeLimit: 0
    };
    setLocalSections([...localSections, newSection]);
    setEditingIndex(localSections.length);
  };

  const updateSection = (index, field, value) => {
    const updatedSections = [...localSections];
    updatedSections[index] = { ...updatedSections[index], [field]: value };
    
    // Auto-calculate total questions and marks if range is valid
    if (field === 'startQuestion' || field === 'endQuestion') {
      const start = parseInt(updatedSections[index].startQuestion);
      const end = parseInt(updatedSections[index].endQuestion);
      if (start && end && start <= end) {
        updatedSections[index].totalQuestions = end - start + 1;
        // Default marks per question (can be customized)
        updatedSections[index].totalMarks = (end - start + 1) * 4;
      }
    }
    
    setLocalSections(updatedSections);
  };

  const removeSection = (index) => {
    const updatedSections = localSections.filter((_, i) => i !== index);
    setLocalSections(updatedSections);
    if (editingIndex === index) {
      setEditingIndex(-1);
    } else if (editingIndex > index) {
      setEditingIndex(editingIndex - 1);
    }
  };

  const saveSections = () => {
    if (Object.keys(errors).length === 0) {
      onSectionsChange(localSections);
      setEditingIndex(-1);
    }
  };

  const cancelEdit = () => {
    setLocalSections(sections);
    setEditingIndex(-1);
    setErrors({});
  };

  const getSectionIcon = (type) => {
    const sectionType = sectionTypes.find(st => st.id === type);
    return sectionType ? sectionType.icon : BookOpen;
  };

  const getSectionColor = (type) => {
    const sectionType = sectionTypes.find(st => st.id === type);
    return sectionType ? sectionType.color : 'text-gray-600';
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Test Sections Configuration
        </h3>
        <button
          onClick={addSection}
          className="btn-primary inline-flex items-center"
        >
          <Plus size={16} className="mr-2" />
          Add Section
        </button>
      </div>

      {/* General Error */}
      {errors.general && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2" />
            <span className="text-red-800 dark:text-red-200">{errors.general}</span>
          </div>
        </div>
      )}

      {/* Sections List */}
      <div className="space-y-3">
        {localSections.map((section, index) => (
          <motion.div
            key={section.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center space-x-3">
                {React.createElement(getSectionIcon(section.type), {
                  className: `h-5 w-5 ${getSectionColor(section.type)}`
                })}
                <span className="font-medium text-gray-900 dark:text-white">
                  Section {index + 1}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                {editingIndex === index ? (
                  <>
                    <button
                      onClick={saveSections}
                      className="p-1 text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                    >
                      <Check size={16} />
                    </button>
                    <button
                      onClick={cancelEdit}
                      className="p-1 text-gray-600 hover:text-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                    >
                      <X size={16} />
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => setEditingIndex(index)}
                      className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => removeSection(index)}
                      className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                    >
                      <Trash2 size={16} />
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Section Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Section Name
                </label>
                <input
                  type="text"
                  value={section.name}
                  onChange={(e) => updateSection(index, 'name', e.target.value)}
                  disabled={editingIndex !== index}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    errors[`name_${index}`] 
                      ? 'border-red-300 dark:border-red-600' 
                      : 'border-gray-300 dark:border-gray-600'
                  } ${editingIndex !== index ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-700'}`}
                  placeholder="e.g., English Language"
                />
                {errors[`name_${index}`] && (
                  <p className="text-red-600 text-xs mt-1">{errors[`name_${index}`]}</p>
                )}
              </div>

              {/* Section Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Section Type
                </label>
                <select
                  value={section.type}
                  onChange={(e) => updateSection(index, 'type', e.target.value)}
                  disabled={editingIndex !== index}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                    editingIndex !== index ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-700'
                  } border-gray-300 dark:border-gray-600`}
                >
                  {sectionTypes.map(type => (
                    <option key={type.id} value={type.id}>
                      {type.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Question Range */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Question Range
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="number"
                    value={section.startQuestion}
                    onChange={(e) => updateSection(index, 'startQuestion', e.target.value)}
                    disabled={editingIndex !== index}
                    min="1"
                    max={totalQuestions}
                    className={`w-20 px-2 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors[`range_${index}`] 
                        ? 'border-red-300 dark:border-red-600' 
                        : 'border-gray-300 dark:border-gray-600'
                    } ${editingIndex !== index ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-700'}`}
                    placeholder="1"
                  />
                  <span className="text-gray-500">to</span>
                  <input
                    type="number"
                    value={section.endQuestion}
                    onChange={(e) => updateSection(index, 'endQuestion', e.target.value)}
                    disabled={editingIndex !== index}
                    min="1"
                    max={totalQuestions}
                    className={`w-20 px-2 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                      errors[`range_${index}`] 
                        ? 'border-red-300 dark:border-red-600' 
                        : 'border-gray-300 dark:border-gray-600'
                    } ${editingIndex !== index ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-700'}`}
                    placeholder={totalQuestions}
                  />
                </div>
                {errors[`range_${index}`] && (
                  <p className="text-red-600 text-xs mt-1">{errors[`range_${index}`]}</p>
                )}
              </div>

              {/* Section Stats */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Section Stats
                </label>
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  <div>Questions: {section.totalQuestions || 0}</div>
                  <div>Marks: {section.totalMarks || 0}</div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Summary */}
      {localSections.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Section Summary</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-blue-700 dark:text-blue-300">Total Sections:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">
                {localSections.length}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Questions Covered:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">
                {localSections.reduce((sum, s) => sum + (s.totalQuestions || 0), 0)} / {totalQuestions}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Total Marks:</span>
              <span className="ml-2 font-medium text-blue-900 dark:text-blue-100">
                {localSections.reduce((sum, s) => sum + (s.totalMarks || 0), 0)}
              </span>
            </div>
            <div>
              <span className="text-blue-700 dark:text-blue-300">Status:</span>
              <span className={`ml-2 font-medium ${
                Object.keys(errors).length === 0 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {Object.keys(errors).length === 0 ? 'Valid' : 'Invalid'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DynamicSectionConfig;
